import { useMemo, useState, useEffect, useRef, type CSSProperties } from "react";
import { Link, useLocation } from "wouter";
import {
  useProjectCalendar,
  useProjectCalendarSubscription,
  useRotateProjectCalendarSubscription,
  useListCalendarBillingEvents,
  getListCalendarBillingEventsQueryKey,
  type ProjectCalendarItem,
  type ProjectStatus,
  type CalendarBillingEvent,
} from "@workspace/api-client-react";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ProTeaserSection } from "@/components/pro-gate";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  CalendarDays, ChevronLeft, ChevronRight, FolderKanban, AlertCircle,
  Download, Link2, Copy, RefreshCw, Check,
} from "lucide-react";

const ACTIVE_STATUSES = ["lead", "quoted", "approved", "in_progress"] as const;
type ActiveStatus = typeof ACTIVE_STATUSES[number];

const STATUS_LABELS: Record<ProjectStatus, string> = {
  lead: "Lead",
  quoted: "Quoted",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Complete",
  archived: "Archived",
};

const STATUS_COLORS: Record<ProjectStatus, string> = {
  lead: "#94a3b8",
  quoted: "#3b82f6",
  approved: "#8b5cf6",
  in_progress: "#f59e0b",
  complete: "#10b981",
  archived: "#6b7280",
};

const DAY_MS = 24 * 60 * 60 * 1000;

function parseDateOnly(s: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function diffDays(a: Date, b: Date): number {
  return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / DAY_MS);
}

function startOfWeek(d: Date): Date {
  const r = startOfDay(d);
  const day = r.getDay();
  return addDays(r, -day);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateOnly(s: string | null): string {
  const d = parseDateOnly(s);
  return d ? formatDateShort(d) : "—";
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type BillingEventType = CalendarBillingEvent["eventType"];

const BILLING_EVENT_LABELS: Record<BillingEventType, string> = {
  quote_expiration: "Quote expires",
  invoice_due: "Invoice due",
  invoice_recurring: "Recurring invoice",
  invoice_paid: "Invoice paid",
};

const BILLING_EVENT_COLORS: Record<BillingEventType, string> = {
  quote_expiration: "#a855f7", // purple
  invoice_due: "#f97316",      // orange
  invoice_recurring: "#0ea5e9", // sky blue
  invoice_paid: "#10b981",     // green
};

const MAX_DOTS_PER_CELL = 4;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function eventDocPath(ev: CalendarBillingEvent): string {
  return ev.documentType === "estimate"
    ? `/quotes/${ev.documentId}`
    : `/invoices/${ev.documentId}`;
}

interface BarSegment {
  project: ProjectCalendarItem;
  start: Date;
  end: Date;
}

function getEffectiveRange(p: ProjectCalendarItem): { start: Date; end: Date } | null {
  const s = parseDateOnly(p.startDate);
  const e = parseDateOnly(p.targetCompletionDate);
  if (!s && !e) return null;
  const start = s ?? e!;
  const end = e ?? s!;
  if (end.getTime() < start.getTime()) return { start: end, end: start };
  return { start, end };
}

export default function CalendarPage() {
  usePageTitle("Calendar");
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  if (!isPro) {
    return (
      <div>
        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <CalendarDays style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Calendar
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
            See every active project on a single timeline.
          </p>
        </div>
        <ProTeaserSection
          title="Project Calendar"
          description="See every active job on a monthly timeline — spot conflicts, plan ahead, and stay on schedule."
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: "0.5rem" }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", padding: "0.25rem 0" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {Array.from({ length: 35 }, (_, i) => {
              const day = i - 2;
              const hasProject = [3, 4, 5, 10, 11, 12, 17, 18, 24, 25].includes(i);
              return (
                <div key={i} style={{ minHeight: 72, padding: "0.25rem", border: "1px solid hsl(var(--border))", borderRadius: "0.25rem", backgroundColor: "hsl(var(--card))" }}>
                  {day > 0 && day <= 30 && (
                    <>
                      <div style={{ fontSize: "0.75rem", fontWeight: 500, marginBottom: 2 }}>{day}</div>
                      {hasProject && (
                        <div style={{ fontSize: "0.65rem", padding: "1px 4px", borderRadius: 3, backgroundColor: "hsl(var(--primary) / 0.15)", color: "hsl(var(--primary))", fontWeight: 500, overflow: "hidden", whiteSpace: "nowrap" }}>
                          {["Smith Bath", "Lakeside Deck", "Office Paint", "Roof Repair", "Kitchen"][i % 5]}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </ProTeaserSection>
      </div>
    );
  }

  return <CalendarContent />;
}

function useIsNarrow(maxWidth: number = 900) {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(`(max-width: ${maxWidth - 1}px)`);
    const update = () => setIsNarrow(mq.matches);
    update();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    // Fallback for older Safari (<14) which only supports the legacy API.
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, [maxWidth]);
  return isNarrow;
}

function CalendarContent() {
  const [location, navigate] = useLocation();
  const { data: projects, isLoading, isError } = useProjectCalendar();
  const isNarrow = useIsNarrow();
  const pagePadding = isNarrow ? "0.75rem" : "1.5rem";

  // URL-synced state
  const initial = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    // Synchronous narrow check so the initial view state is already "month" on
    // mobile — prevents a transient timeline render before the useIsNarrow
    // effect fires (which starts as false on first paint).
    const narrowAtMount =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(max-width: 899px)").matches
        : false;
    const view = !narrowAtMount && sp.get("view") === "timeline" ? "timeline" : "month";
    const status = sp.get("status");
    return {
      view: view as "month" | "timeline",
      status: status && (ACTIVE_STATUSES as readonly string[]).includes(status) ? status as ActiveStatus : "all" as const,
    };
  }, []);

  const [view, setView] = useState<"month" | "timeline">(initial.view);

  // On narrow screens the timeline view is unusable — force month view and
  // clear any stale ?view=timeline param from the URL.
  useEffect(() => {
    if (isNarrow && view === "timeline") {
      setView("month");
    }
  }, [isNarrow, view]);

  const [statusFilter, setStatusFilter] = useState<ActiveStatus | "all">(initial.status);
  const [refDate, setRefDate] = useState<Date>(() => startOfDay(new Date()));

  // Billing events for the visible month grid (6-week window). Only fetched
  // when the month view is active to keep the timeline view's queries lean.
  const monthGridStart = useMemo(() => startOfWeek(startOfMonth(refDate)), [refDate]);
  const monthGridEnd = useMemo(() => addDays(monthGridStart, 41), [monthGridStart]);
  const billingParams = useMemo(() => ({
    startDate: toIsoDate(monthGridStart),
    endDate: toIsoDate(monthGridEnd),
  }), [monthGridStart, monthGridEnd]);
  const { data: billingEvents } = useListCalendarBillingEvents(billingParams, {
    query: {
      enabled: view === "month",
      queryKey: getListCalendarBillingEventsQueryKey(billingParams),
    },
  });

  // Sync URL
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (view === "timeline") sp.set("view", "timeline"); else sp.delete("view");
    if (statusFilter !== "all") sp.set("status", statusFilter); else sp.delete("status");
    const qs = sp.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
  }, [view, statusFilter, location]);

  const filtered = useMemo(() => {
    const list = projects ?? [];
    if (statusFilter === "all") return list;
    return list.filter((p: ProjectCalendarItem) => p.status === statusFilter);
  }, [projects, statusFilter]);

  const withDates = useMemo(() =>
    filtered.filter((p: ProjectCalendarItem) => p.startDate || p.targetCompletionDate),
  [filtered]);

  const noDates = useMemo(() =>
    filtered.filter((p: ProjectCalendarItem) => !p.startDate || !p.targetCompletionDate),
  [filtered]);

  const goToday = () => setRefDate(startOfDay(new Date()));

  if (isLoading) {
    return (
      <div style={{ padding: pagePadding, textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
        Loading calendar…
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Card>
          <CardContent style={{ padding: "2rem", textAlign: "center" }}>
            <AlertCircle style={{ width: 32, height: 32, margin: "0 auto 0.75rem", color: "#ef4444" }} />
            <p style={{ color: "hsl(var(--muted-foreground))" }}>Failed to load projects.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
        <div>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <CalendarDays style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Calendar
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
            Active projects across their start and target completion dates.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center", marginBottom: "1rem" }}>
        {!isNarrow && (
          <div style={{
            display: "inline-flex",
            border: "1px solid hsl(var(--border))",
            borderRadius: "0.5rem",
            overflow: "hidden",
            flexShrink: 0,
          }}>
            {(["month", "timeline"] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: "0.5rem 0.875rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  background: view === v ? "hsl(var(--primary))" : "transparent",
                  color: view === v ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                  border: "none",
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {v}
              </button>
            ))}
          </div>
        )}

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ActiveStatus | "all")}>
          <SelectTrigger style={{ width: isNarrow ? 160 : 180, maxWidth: "100%" }}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All active statuses</SelectItem>
            {ACTIVE_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={goToday}>Today</Button>

        <CalendarExportControls />

        <div style={{
          marginLeft: isNarrow ? 0 : "auto",
          flexBasis: isNarrow ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.25rem",
        }}>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "center",
          }}>
            <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginRight: "0.25rem" }}>
              Status:
            </span>
            {ACTIVE_STATUSES.map(s => (
              <span key={s} style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.375rem",
                fontSize: "0.75rem",
                color: "hsl(var(--muted-foreground))",
              }}>
                <span style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: STATUS_COLORS[s],
                  display: "inline-block",
                }} />
                {STATUS_LABELS[s]}
              </span>
            ))}
          </div>
          {view === "month" && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
              <BillingEventLegend />
            </div>
          )}
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isNarrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) 280px",
        gap: "1rem",
        alignItems: "start",
      }}>
        <div>
          {view === "month" ? (
            <MonthView
              projects={withDates}
              refDate={refDate}
              setRefDate={setRefDate}
              billingEvents={billingEvents ?? []}
              onSelectProject={(id) => navigate(`/projects/${id}`)}
              isNarrow={isNarrow}
            />
          ) : (
            <TimelineView
              projects={withDates}
              refDate={refDate}
              setRefDate={setRefDate}
              onSelectProject={(id) => navigate(`/projects/${id}`)}
            />
          )}
        </div>
        <NoDatesSidebar
          projects={noDates}
          totalActive={filtered.length}
          hasAnyActive={(projects ?? []).length > 0}
          onCreateProject={() => navigate("/projects?new=1")}
        />
      </div>
    </div>
  );
}

function MonthView({
  projects, refDate, setRefDate, billingEvents, onSelectProject, isNarrow,
}: {
  projects: ProjectCalendarItem[];
  refDate: Date;
  setRefDate: (d: Date) => void;
  billingEvents: CalendarBillingEvent[];
  onSelectProject: (id: number) => void;
  isNarrow: boolean;
}) {
  const today = startOfDay(new Date());
  const monthStart = startOfMonth(refDate);
  const monthEnd = endOfMonth(refDate);
  const gridStart = startOfWeek(monthStart);
  // Build 6 weeks (42 days) so the grid is consistent
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));

  // Group billing events by ISO date so each cell can render its own dots
  // without scanning the full list. Preserves the server's chronological /
  // insertion ordering so dot order is stable across re-renders.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarBillingEvent[]>();
    for (const ev of billingEvents) {
      const list = map.get(ev.date);
      if (list) list.push(ev);
      else map.set(ev.date, [ev]);
    }
    return map;
  }, [billingEvents]);

  // Build segments per week for each project
  const weeks: Date[][] = [];
  for (let w = 0; w < 6; w++) weeks.push(days.slice(w * 7, w * 7 + 7));

  const segmentsByWeek: BarSegment[][] = weeks.map(week => {
    const weekStart = week[0];
    const weekEnd = week[6];
    const segs: BarSegment[] = [];
    for (const p of projects) {
      const r = getEffectiveRange(p);
      if (!r) continue;
      // Clip to week
      const segStart = r.start > weekStart ? r.start : weekStart;
      const segEnd = r.end < weekEnd ? r.end : weekEnd;
      if (segStart > segEnd) continue;
      if (r.end < weekStart || r.start > weekEnd) continue;
      segs.push({ project: p, start: segStart, end: segEnd });
    }
    // Sort by start, then by total range (longer first) so wider bars stack first
    segs.sort((a, b) => {
      const cmp = a.start.getTime() - b.start.getTime();
      if (cmp !== 0) return cmp;
      const ar = getEffectiveRange(a.project)!;
      const br = getEffectiveRange(b.project)!;
      return (br.end.getTime() - br.start.getTime()) - (ar.end.getTime() - ar.start.getTime());
    });
    return segs;
  });

  // Assign each segment a row within its week to avoid overlaps
  const segmentRows: number[][] = segmentsByWeek.map(week => {
    const lanes: Date[] = [];
    return week.map(seg => {
      for (let i = 0; i < lanes.length; i++) {
        if (lanes[i] < seg.start) {
          lanes[i] = seg.end;
          return i;
        }
      }
      lanes.push(seg.end);
      return lanes.length - 1;
    });
  });

  const maxLanes = segmentRows.map(rows => rows.length === 0 ? 0 : Math.max(...rows) + 1);

  return (
    <Card>
      <CardContent style={{ padding: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
            {MONTH_NAMES[refDate.getMonth()]} {refDate.getFullYear()}
          </h2>
          <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
            <Button variant="outline" size="icon" onClick={() => setRefDate(new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1))} aria-label="Previous month">
              <ChevronLeft style={{ width: 16, height: 16 }} />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setRefDate(new Date(refDate.getFullYear(), refDate.getMonth() + 1, 1))} aria-label="Next month">
              <ChevronRight style={{ width: 16, height: 16 }} />
            </Button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderTop: "1px solid hsl(var(--border))", borderLeft: "1px solid hsl(var(--border))" }}>
          {WEEKDAY_LABELS.map(w => (
            <div key={w} style={{
              padding: "0.5rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
              borderRight: "1px solid hsl(var(--border))",
              borderBottom: "1px solid hsl(var(--border))",
              background: "hsl(var(--muted) / 0.3)",
            }}>{w}</div>
          ))}
          {weeks.map((week, wi) => {
            const lanes = maxLanes[wi];
            const rowHeight = Math.max(96, 32 + lanes * 22 + 4 + 18);
            return (
              <WeekRow
                key={wi}
                week={week}
                segments={segmentsByWeek[wi]}
                segmentRows={segmentRows[wi]}
                gridStart={week[0]}
                rowHeight={rowHeight}
                month={refDate.getMonth()}
                today={today}
                eventsByDate={eventsByDate}
                onSelectProject={onSelectProject}
                isNarrow={isNarrow}
              />
            );
          })}
        </div>

      </CardContent>
    </Card>
  );
}

function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setIsTouch(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return isTouch;
}

function describeEvent(ev: CalendarBillingEvent, formatCurrency: (v: number) => string): string {
  const docLabel = ev.documentType === "estimate" ? "Quote" : "Invoice";
  const headline = ev.documentNumber
    ? `${BILLING_EVENT_LABELS[ev.eventType]} — ${docLabel} ${ev.documentNumber}`
    : BILLING_EVENT_LABELS[ev.eventType];
  const parts = [headline, ev.title];
  if (ev.customerName) parts.push(ev.customerName);
  if (ev.amount != null) parts.push(formatCurrency(ev.amount));
  return parts.join(" • ");
}

function DayCell({
  day, isToday, inMonth, dayEvents, isTouch, cellStyleBase, isNarrow, mobileSegments,
}: {
  day: Date;
  isToday: boolean;
  inMonth: boolean;
  dayEvents: CalendarBillingEvent[];
  isTouch: boolean;
  cellStyleBase: CSSProperties;
  isNarrow: boolean;
  mobileSegments: BarSegment[];
}) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const hasEvents = dayEvents.length > 0;

  const visibleBars = mobileSegments.slice(0, 2);
  const extraBars = mobileSegments.length > 2 ? mobileSegments.length - 2 : 0;

  const cellInner = (
    <div style={{
      ...cellStyleBase,
      background: isToday ? "hsl(var(--primary) / 0.08)" : (inMonth ? "transparent" : "hsl(var(--muted) / 0.2)"),
      cursor: isTouch && hasEvents ? "pointer" : undefined,
      ...(isNarrow ? {
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minWidth: 0,
      } : {}),
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.25rem",
        flexWrap: "nowrap",
        overflow: "hidden",
        minWidth: 0,
      }}>
        <div style={{
          fontWeight: isToday ? 700 : 500,
          color: isToday ? "hsl(var(--primary))" : (inMonth ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))"),
          flexShrink: 0,
          lineHeight: 1,
        }}>
          {day.getDate()}
        </div>
        {hasEvents && (
          isTouch
            ? <BillingDotsDisplay events={dayEvents} dimmed={!inMonth} />
            : <DesktopBillingDots events={dayEvents} dimmed={!inMonth} />
        )}
      </div>
      {isNarrow && mobileSegments.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 3, minWidth: 0, overflow: "hidden" }}>
          {visibleBars.map((seg, i) => (
            <div
              key={`${seg.project.id}-${i}`}
              style={{
                height: 6,
                borderRadius: 3,
                background: STATUS_COLORS[seg.project.status],
                opacity: inMonth ? 0.9 : 0.45,
                minWidth: 0,
                flexShrink: 0,
              }}
              aria-hidden="true"
            />
          ))}
          {extraBars > 0 && (
            <span style={{
              fontSize: "0.5rem",
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
              lineHeight: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}>
              +{extraBars} projects
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (isTouch && hasEvents) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${dayEvents.length} billing event${dayEvents.length === 1 ? "" : "s"} on ${formatDateShort(day)}`}
            style={{
              all: "unset",
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
          >
            {cellInner}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="center" style={{ width: 280, padding: "0.5rem", maxHeight: 320, overflowY: "auto" }}>
          <div style={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "hsl(var(--muted-foreground))",
            marginBottom: "0.5rem",
            paddingLeft: "0.125rem",
          }}>
            {formatDateShort(day)}, {day.getFullYear()}
          </div>
          <BillingEventList
            events={dayEvents}
            onNavigate={(ev) => { setOpen(false); navigate(eventDocPath(ev)); }}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return cellInner;
}

function BillingDotsDisplay({ events, dimmed }: { events: CalendarBillingEvent[]; dimmed: boolean }) {
  const overflow = events.length > MAX_DOTS_PER_CELL;
  const visible = overflow ? events.slice(0, MAX_DOTS_PER_CELL - 1) : events;
  const overflowCount = overflow ? events.length - visible.length : 0;
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 3,
      alignItems: "center",
      justifyContent: "flex-end",
      maxWidth: "60%",
      opacity: dimmed ? 0.55 : 1,
    }}>
      {visible.map((ev, i) => (
        <span
          key={`${ev.eventType}-${ev.documentType}-${ev.documentId}-${i}`}
          aria-hidden="true"
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: BILLING_EVENT_COLORS[ev.eventType],
            border: "1px solid hsl(var(--card))",
            boxShadow: "0 0 0 1px hsl(var(--border))",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      ))}
      {overflow && (
        <span
          aria-hidden="true"
          style={{
            height: 14,
            minWidth: 18,
            padding: "0 4px",
            borderRadius: 7,
            background: "hsl(var(--muted))",
            border: "1px solid hsl(var(--border))",
            color: "hsl(var(--foreground))",
            fontSize: "0.625rem",
            fontWeight: 600,
            lineHeight: "12px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
}

function DesktopBillingDots({ events, dimmed }: { events: CalendarBillingEvent[]; dimmed: boolean }) {
  const overflow = events.length > MAX_DOTS_PER_CELL;
  const visible = overflow ? events.slice(0, MAX_DOTS_PER_CELL - 1) : events;
  const remaining = overflow ? events.slice(MAX_DOTS_PER_CELL - 1) : [];
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 3,
      alignItems: "center",
      justifyContent: "flex-end",
      maxWidth: "60%",
      opacity: dimmed ? 0.55 : 1,
    }}>
      {visible.map((ev, i) => (
        <DesktopBillingDot key={`${ev.eventType}-${ev.documentType}-${ev.documentId}-${i}`} event={ev} />
      ))}
      {overflow && <DesktopBillingDotMore events={remaining} />}
    </div>
  );
}

function DesktopBillingDot({ event }: { event: CalendarBillingEvent }) {
  const [, navigate] = useLocation();
  const { formatCurrency } = useSettingsFormat();
  const path = eventDocPath(event);
  const tooltip = describeEvent(event, formatCurrency);
  const color = BILLING_EVENT_COLORS[event.eventType];
  return (
    <button
      type="button"
      aria-label={tooltip}
      title={tooltip}
      onClick={(e) => { e.stopPropagation(); navigate(path); }}
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: color,
        border: "1px solid hsl(var(--card))",
        boxShadow: "0 0 0 1px hsl(var(--border))",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
      }}
      data-testid={`calendar-dot-${event.eventType}-${event.documentId}`}
    />
  );
}

function DesktopBillingDotMore({ events }: { events: CalendarBillingEvent[] }) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const tooltip = `${events.length} more billing event${events.length === 1 ? "" : "s"}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={tooltip}
          title={tooltip}
          onClick={(e) => e.stopPropagation()}
          style={{
            height: 14,
            minWidth: 18,
            padding: "0 4px",
            borderRadius: 7,
            background: "hsl(var(--muted))",
            border: "1px solid hsl(var(--border))",
            color: "hsl(var(--foreground))",
            fontSize: "0.625rem",
            fontWeight: 600,
            cursor: "pointer",
            lineHeight: "12px",
          }}
        >
          +{events.length}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="center" style={{ width: 260, padding: "0.5rem", maxHeight: 280, overflowY: "auto" }}>
        <BillingEventList
          events={events}
          onNavigate={(ev) => { setOpen(false); navigate(eventDocPath(ev)); }}
        />
      </PopoverContent>
    </Popover>
  );
}

function BillingEventList({
  events, onNavigate,
}: {
  events: CalendarBillingEvent[];
  onNavigate: (ev: CalendarBillingEvent) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {events.map((ev, i) => (
        <BillingEventCard
          key={`${ev.eventType}-${ev.documentType}-${ev.documentId}-${i}`}
          event={ev}
          onNavigate={() => onNavigate(ev)}
          compact
        />
      ))}
    </div>
  );
}

function BillingEventCard({
  event, onNavigate, compact,
}: {
  event: CalendarBillingEvent;
  onNavigate: () => void;
  compact?: boolean;
}) {
  const { formatCurrency } = useSettingsFormat();
  const color = BILLING_EVENT_COLORS[event.eventType];
  const docLabel = event.documentType === "estimate" ? "Quote" : "Invoice";
  return (
    <button
      type="button"
      onClick={onNavigate}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.125rem",
        textAlign: "left",
        padding: compact ? "0.375rem" : "0.25rem",
        background: "transparent",
        border: compact ? "1px solid hsl(var(--border))" : "none",
        borderRadius: 4,
        cursor: "pointer",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
        <span style={{ fontSize: "0.6875rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>
          {BILLING_EVENT_LABELS[event.eventType]}
          {event.documentNumber ? ` — ${docLabel} ${event.documentNumber}` : ""}
        </span>
      </div>
      <div style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{event.title}</div>
      {event.customerName && (
        <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{event.customerName}</div>
      )}
      {event.amount != null && (
        <div style={{ fontSize: "0.75rem", color: "hsl(var(--foreground))" }}>{formatCurrency(event.amount)}</div>
      )}
    </button>
  );
}

function BillingEventLegend() {
  return (
    <div style={{
      display: "inline-flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      alignItems: "center",
      fontSize: "0.75rem",
      color: "hsl(var(--muted-foreground))",
    }}>
      <span style={{ fontWeight: 600 }}>Billing dots:</span>
      {(Object.keys(BILLING_EVENT_LABELS) as BillingEventType[]).map(t => (
        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
          <span style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: BILLING_EVENT_COLORS[t],
          }} />
          {BILLING_EVENT_LABELS[t]}
        </span>
      ))}
    </div>
  );
}

function WeekRow({
  week, segments, segmentRows, gridStart, rowHeight, month, today, eventsByDate, onSelectProject, isNarrow,
}: {
  week: Date[];
  segments: BarSegment[];
  segmentRows: number[];
  gridStart: Date;
  rowHeight: number;
  month: number;
  today: Date;
  eventsByDate: Map<string, CalendarBillingEvent[]>;
  onSelectProject: (id: number) => void;
  isNarrow: boolean;
}) {
  const cellStyleBase: CSSProperties = isNarrow
    ? {
        borderRight: "1px solid hsl(var(--border))",
        borderBottom: "1px solid hsl(var(--border))",
        padding: "0.25rem 0.25rem",
        fontSize: "0.6875rem",
        aspectRatio: "1",
        overflow: "hidden",
        boxSizing: "border-box",
      }
    : {
        borderRight: "1px solid hsl(var(--border))",
        borderBottom: "1px solid hsl(var(--border))",
        padding: "0.25rem 0.375rem",
        fontSize: "0.75rem",
      };
  const isTouch = useIsTouchDevice();

  // Build a map from ISO date → segments touching that day for mobile inline bars
  const segmentsByDay = useMemo<Map<string, BarSegment[]>>(() => {
    if (!isNarrow) return new Map();
    const map = new Map<string, BarSegment[]>();
    for (const day of week) {
      const key = toIsoDate(day);
      const touching = segments.filter(seg =>
        day >= seg.start && day <= seg.end,
      );
      if (touching.length > 0) map.set(key, touching);
    }
    return map;
  }, [isNarrow, week, segments]);

  return (
    <div style={{
      gridColumn: "1 / -1",
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      position: "relative",
      ...(isNarrow ? {} : { minHeight: rowHeight }),
    }}>
      {week.map((day) => {
        const isToday = isSameDay(day, today);
        const inMonth = day.getMonth() === month;
        const dayEvents = eventsByDate.get(toIsoDate(day)) ?? [];
        const mobileSegments = isNarrow ? (segmentsByDay.get(toIsoDate(day)) ?? []) : [];
        return (
          <DayCell
            key={day.toISOString()}
            day={day}
            isToday={isToday}
            inMonth={inMonth}
            dayEvents={dayEvents}
            isTouch={isTouch}
            cellStyleBase={cellStyleBase}
            isNarrow={isNarrow}
            mobileSegments={mobileSegments}
          />
        );
      })}
      {!isNarrow && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {segments.map((seg, i) => {
            const startCol = diffDays(seg.start, gridStart);
            const endCol = diffDays(seg.end, gridStart);
            const left = (startCol / 7) * 100;
            const width = ((endCol - startCol + 1) / 7) * 100;
            const lane = segmentRows[i];
            // 32 px matches the header-height assumption used in rowHeight so bars
            // always start below the day-number / billing-dot strip.
            const top = 32 + lane * 22;
            const range = getEffectiveRange(seg.project)!;
            return (
              <button
                key={`${seg.project.id}-${i}`}
                onClick={() => onSelectProject(seg.project.id)}
                title={`${seg.project.name}\n${formatDateOnly(seg.project.startDate)} → ${formatDateOnly(seg.project.targetCompletionDate)}`}
                style={{
                  position: "absolute",
                  left: `calc(${left}% + 2px)`,
                  width: `calc(${width}% - 4px)`,
                  top,
                  height: 18,
                  background: STATUS_COLORS[seg.project.status],
                  color: "#fff",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  padding: "0 0.375rem",
                  borderRadius: 3,
                  border: "none",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  lineHeight: "18px",
                  opacity: 0.95,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.95"; }}
              >
                {seg.project.name}
                {seg.start.getTime() === range.start.getTime() ? "" : " ▸"}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimelineView({
  projects, refDate, setRefDate, onSelectProject,
}: {
  projects: ProjectCalendarItem[];
  refDate: Date;
  setRefDate: (d: Date) => void;
  onSelectProject: (id: number) => void;
}) {
  const today = startOfDay(new Date());
  const TOTAL_WEEKS = 10;
  const startWeek = startOfWeek(refDate);
  const days: Date[] = [];
  for (let i = 0; i < TOTAL_WEEKS * 7; i++) days.push(addDays(startWeek, i));
  const rangeEnd = addDays(startWeek, TOTAL_WEEKS * 7 - 1);

  const visible = projects.filter(p => {
    const r = getEffectiveRange(p);
    if (!r) return false;
    return !(r.end < startWeek || r.start > rangeEnd);
  });

  const COL_WIDTH = 80; // per week
  const dayWidth = COL_WIDTH / 7;
  const leftPaneWidth = 220;

  return (
    <Card>
      <CardContent style={{ padding: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem", gap: "0.5rem" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
            {formatDateShort(startWeek)} – {formatDateShort(rangeEnd)}, {rangeEnd.getFullYear()}
          </h2>
          <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
            <Button variant="outline" size="icon" onClick={() => setRefDate(addDays(refDate, -TOTAL_WEEKS * 7))} aria-label="Previous period">
              <ChevronLeft style={{ width: 16, height: 16 }} />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setRefDate(addDays(refDate, TOTAL_WEEKS * 7))} aria-label="Next period">
              <ChevronRight style={{ width: 16, height: 16 }} />
            </Button>
          </div>
        </div>

        {visible.length === 0 ? (
          <p style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
            No projects fall within this period. Use the arrows or the Today button to navigate.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: leftPaneWidth + COL_WIDTH * TOTAL_WEEKS }}>
              {/* Header row */}
              <div style={{ display: "flex", borderBottom: "1px solid hsl(var(--border))", position: "sticky", top: 0, background: "hsl(var(--card))", zIndex: 1 }}>
                <div style={{ width: leftPaneWidth, flexShrink: 0, padding: "0.5rem", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>
                  Project
                </div>
                {Array.from({ length: TOTAL_WEEKS }).map((_, i) => {
                  const weekDay = addDays(startWeek, i * 7);
                  return (
                    <div key={i} style={{
                      width: COL_WIDTH,
                      flexShrink: 0,
                      padding: "0.375rem 0.5rem",
                      fontSize: "0.6875rem",
                      color: "hsl(var(--muted-foreground))",
                      borderLeft: "1px solid hsl(var(--border))",
                      textAlign: "left",
                    }}>
                      {formatDateShort(weekDay)}
                    </div>
                  );
                })}
              </div>

              {/* Project rows */}
              <div style={{ position: "relative" }}>
                {/* Today marker */}
                {today >= startWeek && today <= rangeEnd && (
                  <div style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: leftPaneWidth + diffDays(today, startWeek) * dayWidth + dayWidth / 2,
                    width: 2,
                    background: "hsl(var(--primary))",
                    pointerEvents: "none",
                    zIndex: 2,
                  }}>
                    <div style={{
                      position: "absolute",
                      top: -10,
                      left: -16,
                      background: "hsl(var(--primary))",
                      color: "hsl(var(--primary-foreground))",
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      padding: "0 0.375rem",
                      borderRadius: 2,
                    }}>
                      Today
                    </div>
                  </div>
                )}

                {visible.map(p => {
                  const r = getEffectiveRange(p)!;
                  const segStart = r.start > startWeek ? r.start : startWeek;
                  const segEnd = r.end < rangeEnd ? r.end : rangeEnd;
                  const startOffset = diffDays(segStart, startWeek);
                  const span = diffDays(segEnd, segStart) + 1;
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid hsl(var(--border))", height: 44, position: "relative" }}>
                      <Link href={`/projects/${p.id}`}>
                        <div style={{
                          width: leftPaneWidth,
                          flexShrink: 0,
                          padding: "0.5rem",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                        }} title={p.name}>
                          {p.name}
                          {p.customerName && (
                            <div style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", fontWeight: 400 }}>
                              {p.customerName}
                            </div>
                          )}
                        </div>
                      </Link>
                      <div style={{ position: "relative", flex: 1, height: "100%" }}>
                        {/* week column dividers */}
                        {Array.from({ length: TOTAL_WEEKS }).map((_, i) => (
                          <div key={i} style={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: i * COL_WIDTH,
                            width: 1,
                            background: "hsl(var(--border))",
                          }} />
                        ))}
                        <button
                          onClick={() => onSelectProject(p.id)}
                          title={`${p.name}\n${formatDateOnly(p.startDate)} → ${formatDateOnly(p.targetCompletionDate)}`}
                          style={{
                            position: "absolute",
                            left: startOffset * dayWidth + 2,
                            width: span * dayWidth - 4,
                            top: 10,
                            height: 24,
                            background: STATUS_COLORS[p.status],
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            padding: "0 0.5rem",
                            cursor: "pointer",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            opacity: 0.95,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.95"; }}
                        >
                          {STATUS_LABELS[p.status]}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CalendarExportControls() {
  const { toast } = useToast();
  const { data, isLoading, isError, refetch } = useProjectCalendarSubscription();
  const rotate = useRotateProjectCalendarSubscription({
    onSuccess: () => {
      toast({
        title: "Subscription link rotated",
        description: "Old links no longer work — share the new URL with your calendar app.",
      });
      refetch();
    },
    onError: (err: unknown) => {
      toast({
        title: "Could not rotate link",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    },
  });

  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
  }, []);

  const handleCopy = async () => {
    if (!data?.subscriptionUrl) return;
    try {
      await navigator.clipboard.writeText(data.subscriptionUrl);
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the link and copy it manually.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = () => {
    if (!data?.downloadUrl) return;
    // Trigger an .ics download — point a hidden link at the same URL the
    // subscription uses; the server already sets a Content-Disposition header.
    const a = document.createElement("a");
    a.href = data.downloadUrl;
    a.download = "quotrak-projects.ics";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);

  const handleRotate = () => {
    if (rotate.isPending) return;
    rotate.mutate(undefined, {
      onSuccess: () => setConfirmRotateOpen(false),
    });
  };

  return (
    <>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" aria-label="Export calendar">
          <Download style={{ width: 14, height: 14, marginRight: 6 }} />
          Export
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" style={{ width: 360 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <div>
            <h3 style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.25rem" }}>
              Export to your calendar
            </h3>
            <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>
              Subscribe so projects update automatically in Google Calendar, Apple Calendar, or Outlook.
              Or download a one-time .ics file.
            </p>
          </div>

          {isLoading && (
            <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Preparing your link…</p>
          )}

          {isError && (
            <p style={{ fontSize: "0.8125rem", color: "#ef4444" }}>
              Couldn't load your subscription link. Please retry.
            </p>
          )}

          {data && (
            <>
              <div>
                <label
                  htmlFor="calendar-subscription-url"
                  style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.75rem", fontWeight: 500, marginBottom: "0.375rem" }}
                >
                  <Link2 style={{ width: 12, height: 12 }} />
                  Subscription URL
                </label>
                <div style={{ display: "flex", gap: "0.375rem" }}>
                  <Input
                    id="calendar-subscription-url"
                    readOnly
                    value={data.subscriptionUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ fontFamily: "monospace", fontSize: "0.6875rem" }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                    aria-label={copied ? "Copied" : "Copy subscription URL"}
                    title={copied ? "Copied!" : "Copy"}
                  >
                    {copied
                      ? <Check style={{ width: 14, height: 14, color: "#10b981" }} />
                      : <Copy style={{ width: 14, height: 14 }} />}
                  </Button>
                </div>
                <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginTop: "0.375rem", lineHeight: 1.4 }}>
                  In Google Calendar, choose <em>Other calendars → From URL</em>. In Apple Calendar, choose
                  <em> File → New Calendar Subscription</em>. Calendars typically refresh every hour.
                </p>
              </div>

              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
                  <Download style={{ width: 14, height: 14, marginRight: 6 }} />
                  Download .ics
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmRotateOpen(true)}
                  disabled={rotate.isPending}
                >
                  <RefreshCw style={{ width: 14, height: 14, marginRight: 6 }} />
                  {rotate.isPending ? "Rotating…" : "Rotate link"}
                </Button>
              </div>

              <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>
                Treat this link like a password — anyone with it can read your active project schedule.
                Rotate it any time to revoke access.
              </p>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
    <AlertDialog open={confirmRotateOpen} onOpenChange={(open) => { if (!rotate.isPending) setConfirmRotateOpen(open); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rotate subscription link?</AlertDialogTitle>
          <AlertDialogDescription>
            Calendars using the old URL will stop receiving updates until you re-subscribe with the new link.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={rotate.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleRotate(); }}
            disabled={rotate.isPending}
          >
            {rotate.isPending ? "Rotating…" : "Rotate link"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function NoDatesSidebar({ projects, totalActive, hasAnyActive, onCreateProject }: {
  projects: ProjectCalendarItem[];
  totalActive: number;
  hasAnyActive: boolean;
  onCreateProject: () => void;
}) {
  if (!hasAnyActive) {
    return (
      <Card>
        <CardContent style={{ padding: "1.25rem", textAlign: "center" }}>
          <FolderKanban style={{ width: 32, height: 32, margin: "0 auto 0.75rem", color: "hsl(var(--muted-foreground))" }} />
          <h3 style={{ fontSize: "0.9375rem", fontWeight: 600, marginBottom: "0.375rem" }}>No active projects yet</h3>
          <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.875rem" }}>
            Create a project to start seeing it on the calendar.
          </p>
          <Button size="sm" onClick={onCreateProject}>Create your first project</Button>
        </CardContent>
      </Card>
    );
  }

  if (projects.length === 0) {
    if (totalActive === 0) return null;
    return (
      <Card>
        <CardContent style={{ padding: "1rem" }}>
          <h3 style={{ fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.5rem" }}>All projects have dates</h3>
          <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
            Every active project shows up on the calendar.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent style={{ padding: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <AlertCircle style={{ width: 16, height: 16, color: "#f59e0b" }} />
          <h3 style={{ fontSize: "0.875rem", fontWeight: 600 }}>Missing dates ({projects.length})</h3>
        </div>
        <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.75rem" }}>
          These active projects are missing a start or target completion date, so their range on the calendar is incomplete.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {projects.map(p => {
            const missing = !p.startDate && !p.targetCompletionDate
              ? "No dates"
              : !p.startDate ? "No start date" : "No end date";
            return (
              <Link key={p.id} href={`/projects/${p.id}`}>
                <div style={{
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.375rem",
                  padding: "0.5rem 0.625rem",
                  cursor: "pointer",
                  background: "hsl(var(--card))",
                }}>
                  <div style={{ fontSize: "0.8125rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", marginTop: 2 }}>
                    <span style={{
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      padding: "0.0625rem 0.375rem",
                      borderRadius: "0.25rem",
                      background: STATUS_COLORS[p.status] + "22",
                      color: STATUS_COLORS[p.status],
                    }}>{STATUS_LABELS[p.status]}</span>
                    <span style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))" }}>{missing}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
